import { z } from "zod";

export const ADMIN_USER_STATUSES = ["ACTIVE", "DISABLED"] as const;
export const ADMIN_ROLE_CODES = ["SUPER", "OPERATOR", "CS", "VIEWER"] as const;

export type AdminUserStatusFilter = "" | (typeof ADMIN_USER_STATUSES)[number];
export type AdminRoleCode = (typeof ADMIN_ROLE_CODES)[number];
export type AdminRoleFilter = "" | AdminRoleCode;

export interface AdminUserCriteria {
  readonly status: AdminUserStatusFilter;
  readonly roleCode: AdminRoleFilter;
  readonly page: number;
  readonly size: number;
}

export interface CreateAdminValues {
  readonly loginId: string;
  readonly name: string;
  readonly email: string;
  readonly initialPassword: string;
  readonly roleCodes: readonly AdminRoleCode[];
}

const passwordPolicy = (value: string) => (
  /\p{Lu}/u.test(value)
  && /\p{Ll}/u.test(value)
  && /\p{Nd}/u.test(value)
  && Array.from(value).some((character) => !/[\p{L}\p{N}]/u.test(character))
);

export const createAdminSchema = z.object({
  loginId: z.string().trim().min(1, "로그인 계정을 입력해 주세요.").max(100, "로그인 계정은 100자까지 입력할 수 있습니다."),
  name: z.string().trim().min(1, "이름을 입력해 주세요.").max(100, "이름은 100자까지 입력할 수 있습니다."),
  email: z.string().trim().max(255, "이메일은 255자까지 입력할 수 있습니다.").refine((value) => value === "" || z.email().safeParse(value).success, "이메일 형식을 확인해 주세요."),
  initialPassword: z.string()
    .min(8, "초기 비밀번호를 8자 이상 입력해 주세요.")
    .max(30, "초기 비밀번호는 30자까지 입력할 수 있습니다.")
    .refine(passwordPolicy, "영문 대문자·소문자·숫자·특수문자를 모두 포함해 주세요."),
  roleCodes: z.array(z.enum(ADMIN_ROLE_CODES)).min(1, "역할을 하나 이상 선택해 주세요."),
});

export const adminRolesSchema = z.object({
  roleCodes: z.array(z.enum(ADMIN_ROLE_CODES)).min(1, "역할을 하나 이상 선택해 주세요."),
});

export const disableAdminSchema = z.object({
  reason: z.string().trim().min(1, "비활성화 사유를 입력해 주세요.").max(500, "비활성화 사유는 500자까지 입력할 수 있습니다."),
});

export const resetAdminTotpSchema = z.object({
  reauthenticationPassword: z.string().min(1, "현재 관리자 비밀번호를 입력해 주세요.").max(200, "비밀번호는 200자까지 입력할 수 있습니다."),
});

export function parseAdminUserStatus(value: string | null): AdminUserStatusFilter {
  return ADMIN_USER_STATUSES.includes(value as (typeof ADMIN_USER_STATUSES)[number])
    ? value as AdminUserStatusFilter
    : "";
}

export function parseAdminRoleCode(value: string | null): AdminRoleFilter {
  return ADMIN_ROLE_CODES.includes(value as AdminRoleCode) ? value as AdminRoleCode : "";
}

export function parseAdminUserPage(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return 0;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
}

export function parseAdminUserId(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function isAdminRoleCode(value: string): value is AdminRoleCode {
  return ADMIN_ROLE_CODES.includes(value as AdminRoleCode);
}
